/**
 * mathjs, restricted to the number-only build.
 *
 * The full `mathjs` entry point pulls BigNumber, Fraction, Complex, units and the matrix stack.
 * This app only ever calls `add`, `multiply`, `divide` and `format` on plain numbers, so the number
 * build is sufficient and roughly half the cost, and it produces identical results for those
 * operations. Note this is NOT self-guarding at compile time: the `mathjs/number` types resolve to
 * mathjs's full `index.d.ts`, so `math.bignumber(...)` type-checks fine and is `undefined` at
 * runtime. Keep to plain-number arithmetic here.
 *
 * The `mathjs/number` specifier gets its types from the AMBIENT declaration in ./mathjs-number.d.ts,
 * not from a tsconfig `paths` entry. This matters, and is the whole story behind ONBCOO-15247: a `paths`
 * entry mapping this specifier to mathjs's `.d.ts` is also applied to *webpack's* module resolution
 * by `@ngtools/webpack`'s `TypeScriptPathsPlugin`, so the runtime import resolved to a declaration
 * file — an empty module, emitted with no build error. `create` was then `undefined` in the browser,
 * and every call site threw `TypeError: create is not a function`. Verified by building twice and
 * grepping `dist` for a mathjs runtime string: absent with the `paths` entry, present without it.
 *
 * Neither gate could see it. `tsc` passed because the mapping pointed at valid full-API types; all
 * 11,662 Jest tests passed because Jest resolves this subpath through Node to the real CommonJS
 * build. Only E2E caught it, and only in the internal-transfer flow, since every `math.*` call site
 * in the repo is on that path. It surfaced two ways: thrown in a component method it logged a
 * TypeError and produced $0.00 estimates that failed validation; thrown inside an effect's
 * `switchMap` it was swallowed by `catchError`, so the success action never fired, `closeSpinner()`
 * never ran, and the page hung behind a loading scrim with nothing in the console.
 *
 * The original of this file also deferred the load behind `require('mathjs/number')` in a `Proxy`
 * get trap, to keep Jest suites from loading mathjs to reach helpers they never call into. That was
 * not the root cause, but keep this import STATIC anyway: with the empty-module bug in play it moved
 * the same throw to app bootstrap, and it makes any future resolution fault fail at startup rather
 * than at a call site. If the Jest cost is worth reclaiming, prove it with a full E2E run.
 *
 * `math.format(x, { notation: 'fixed', precision: 2 })` in particular must keep coming from mathjs.
 * It rounds half-up on the decimal representation (2.675 -> "2.68") where `Number.toFixed` rounds
 * on the binary double (-> "2.67"), and the two disagree on a meaningful fraction of money-shaped
 * values — so this is not a dependency that can be swapped for `toFixed`.
 */
import { all, ConfigOptions, create } from 'mathjs/number';

const config: ConfigOptions = {
  precision: 4,
};

export const math = create(all, config);
